import { Progress, rem, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useAtomValue } from 'jotai';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { isNullish } from 'remeda';

import { ublProgressAtom } from './ublProgressAtom';

const PROGRESS_NOTIFICATION_ID = 'ubl-import-progress';
const UBL_IMPORT_PATH = '/import/invoices/ubl';

export const UblImportProgressNotifier = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const progress = useAtomValue(ublProgressAtom);

  const isOnImportPage = pathname === UBL_IMPORT_PATH;
  const isImporting = progress.active && isNullish(progress.errors);
  const hasErrors = Array.isArray(progress.errors) && progress.errors.length > 0;
  const value = Math.round(progress.value ?? 0);

  const isNotificationShown = useRef(false);
  const hadErrors = useRef(false);

  useEffect(() => {
    if (isImporting && !isOnImportPage) {
      const payload = {
        id: PROGRESS_NOTIFICATION_ID,
        color: 'purple',
        loading: true,
        withCloseButton: false,
        autoClose: false as const,
        title: t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.IMPORTING),
        message: (
          <Stack gap={rem(4)} mt={rem(4)}>
            <Progress value={value} size="sm" radius="xl" color="purple" striped animated />
            <Text size="xs" c="dimmed" ta="right">
              {value}%
            </Text>
            <Text size="xs" c="dimmed">
              {t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.KEEP_OPEN)}
            </Text>
          </Stack>
        ),
      };

      if (isNotificationShown.current) {
        notifications.update(payload);
      } else {
        notifications.show(payload);
        isNotificationShown.current = true;
      }
    } else if (isNotificationShown.current) {
      notifications.hide(PROGRESS_NOTIFICATION_ID);
      isNotificationShown.current = false;
    }
  }, [isImporting, isOnImportPage, value, t]);

  useEffect(() => {
    if (hasErrors && !hadErrors.current && !isOnImportPage) {
      notifications.show({
        color: 'red',
        title: t(i18nKeys.ERROR.IMPORT_ERROR),
        message: t(i18nKeys.INVOICING.IMPORT.UBL_UPLOAD.IMPORT_FAILED),
      });
    }
    hadErrors.current = hasErrors;
  }, [hasErrors, isOnImportPage, t]);

  return null;
};
